<template>
    <div class="puf-verify-page">
        <ProgressBar
            v-if="!lockedOut"
            :current-step="1"
            :active-step-color="activeStepColor"
            :vertical-rule-active-color="activeStepColor"
            :progress-color="progressColor"
        />
        <div class="puf-verify-content">
            <Locked v-if="lockedOut" />
            <div v-else>
                <h1>
                    First, let's find your GeneSight test order
                </h1>
                <p class="puf-copy">
                    We'll use your name, date of birth, and ZIP code to locate your order and guide you through the next steps.
                </p>
                <InvalidInfo v-if="invalidData" />
                <form-validate
                    class-name="patient-information-verify-info-form"
                    id-name="payment-verify"
                    @no-errors-callback="removeErrorBanner"
                    @invalid-callback="showFormErrors"
                    @valid-callback="verifyInfo"
                >
                    <full-name
                        class="demographic-full-name"
                        error-string="Invalid Name"
                        :first-name="this.$store.getters.pufFirstname"
                        :last-name="this.$store.getters.pufLastname"
                        :disable-inputs="disableInputs"
                        :is-vertical="true"
                        :show-notice="false"
                        label="Patient name"
                        @firstNameInput="setFirstName"
                        @lastNameInput="setLastName"
                    />
                    <dob-comp
                        store-setting-string="setPufDob"
                        store-getter-string="pufDob"
                        :has-error="errorInputs"
                        :disable="disableInputs"
                        :is-vertical="true"
                        label="Patient date of birth"
                        @DobInputChange="errorInputs = false"
                    />
                    <div v-container-validation class="input-row is-vertical">
                        <label class="input-label label">
                            Zip code
                        </label>
                        <input ref="pufZip"
                               v-model="zip"
                               v-mask="'#####'"
                               v-max-min-length-validation="{maxLength: 5, minLength: 5, required: true}"
                               :disabled="disableInputs"
                               class="text-input"
                               name="zip"
                               type="text" placeholder="Zip"
                        >
                        <error-labels invalid-string="Invalid Zip" :is-vertical="true" />
                    </div>
                    <div class="input-row" style="margin-bottom: 0px">
                        <div>
                            <button class="submit-btn puf-button" type="submit" :disabled="disableInputs">
                                <p class="submit-btn-text">
                                    Find my order
                                </p>
                            </button>
                        </div>
                    </div>
                </form-validate>
            </div>
        </div>
    </div>
</template>

<script>
import DobComp from "../components/layout/Demographic/DobInput.vue";
import FullName from "../components/layout/Demographic/FullNameInputs.vue";
import PaymentHttp from "../scripts/http/payment-http.js";
import moment from "moment";
import CommonFunc from "../scripts/common.js";
import ErrorLabels from "../components/layout/Demographic/ErrorLabels.vue";
import ProgressBar from "../components/ui/ProgressBar.vue";
import InvalidInfo from "../components/layout/Payment/InvalidInfo.vue";
import Locked from "../components/layout/Payment/Locked.vue";

export default {
    name: "PaymentVerify",
    components: {
        DobComp,
        FullName,
        ErrorLabels,
        ProgressBar,
        InvalidInfo,
        Locked
    },
    data() {
        return {
            disableInputs: false,
            errorInputs: false,
            invalidData: false,
            lockedOut: false
        };
    },
    computed: {
        zip: {
            get() {
                return this.$store.getters.pufZip;
            },
            set(value) {
                this.$store.commit("setPufZip", value);
            }
        },
        activeStepColor: {
            get() {
                return this.invalidData ? "#FF9C66" : "#0691EF";
            }
        },
        progressColor: {
            get() {
                return this.invalidData ? "#FF9C66" : "#5EB1F3";
            }
        }
    },
    created() {
        CommonFunc.grabPatientToken((token, origin)=> {
            this.$store.dispatch("setPufToken", token);
            this.$store.dispatch("setPatientToken", {token, origin});
        });
        this.checkValidPufToken();
    },
    mounted() {
        this.showExpiredInfoError();
    },
    methods: {
        checkValidPufToken() {
            PaymentHttp.getIsValidToken().then(response => {
                if(response.data.result != "undefined" && response.data.result != "Unauthorized") {
                    this.$store.dispatch("setAntiforgeryToken", response.data.antiforgeryToken);
                    let attempts = parseInt(response.data.attempts);
                    let maxAttempts = parseInt(response.data.maxAttempts);
                    this.$store.dispatch("setPufValidationAttempts", {maxAttempts, attempts});
                    this.$store.dispatch("setShowStartupLoader", false);
                    
                    if (response.data.status === "Completed") {
                        this.$store.dispatch("setPufIsComplete", true);
                        this.goToThankYouPage();
                    }
                }
                else {
                    this.goToUnauthorizedPage();
                }
            }).catch(()=> {
                this.goToUnauthorizedPage();
            });
        },
        showFormErrors() {
            CommonFunc.showFormErrors(this);
        },
        removeErrorBanner() {
            CommonFunc.removeErrorBanner(this);
        },
        showExpiredInfoError() {
            let attempts = this.$store.getters.currentPufValidationAttempts;
            let maxAttempts = this.$store.getters.maxPufValidationAttempts;
            this.errorInputs = false;
            this.disableInputs = false;
            
            if(attempts > 0) {
                if(attempts >= maxAttempts) {
                    this.disableInputs = true;
                    this.lockedOut = true;
                } 
                this.invalidData = true;
                this.$store.dispatch("setShowErrorMessage", false);
            }
        },
        verifyInfo() {
            var data = {
                FirstName: this.$store.getters.pufFirstName,
                LastName: this.$store.getters.pufLastName,
                DateOfBirth: moment(this.$store.getters.pufDob, "MM/DD/YYYY").format("YYYY-MM-DD"),
                PostalCode: this.$store.getters.pufZip
            };

            this.$store.dispatch("setShowStartupLoader", true);
            PaymentHttp.verifyInformation(data).then((response)=> {
                if (response && response.data && response.data.verified) {
                    this.$store.dispatch("setShowErrorMessage", false);
                    this.invalidData = true;
                    this.$store.dispatch("setAmount", response.data.amount);
                    this.$store.dispatch("setXifinConfig", response.data.xifinConfig);
                    if (response.data.braintreeClientToken) {
                        this.$store.dispatch("setBraintreeClientToken", response.data.braintreeClientToken);
                    }
                    if(response.data.tokenData && response.data.tokenData.success){
                        this.$store.dispatch("setXifinPrepaymentToken", response.data.tokenData.token);
                        this.$store.dispatch("setPrepaymentTokenExp", response.data.tokenData.millisecondsToExpiration);
                    }
                    this.$store.dispatch("setPufIsPatientVerified", true);
                    this.$router.push("/payment-review-cost");
                } else {
                    this.$router.push("/error");
                }
            }).catch((errorResponse)=> {
                if(errorResponse && errorResponse.response && errorResponse.response.data) {
                    let attempts = parseInt(errorResponse.response.data.attempts);
                    let maxAttempts = parseInt(errorResponse.response.data.maxAttempts);
                    this.$store.dispatch("setPufValidationAttempts", {maxAttempts, attempts});
                    this.showExpiredInfoError();
                }
                else {
                    this.$router.push("/error");
                }
            }).finally(()=> {
                this.$store.dispatch("setShowStartupLoader", false);
            });
        },
        goToUnauthorizedPage() {
            this.$store.dispatch("setShowStartupLoader", false);
            this.$router.push({
                name: "Error",
                params: {
                    expiredVersion: true
                }
            });
        },
        setFirstName(value) {
            this.$store.dispatch("setPufFirstName", value);
        },
        setLastName(value){
            this.$store.dispatch("setPufLastName", value);
        },
        setZip(value){
            this.$store.dispatch("setPufZip", value);
        },
        goToThankYouPage() {
            this.$router.push("/payment-complete");
        }
    }
};
</script>

<style lang="scss" scoped>
    @import "../styles/inputs.scss";

    .puf-verify-page {
        @include site-section();
        padding-bottom: 50px;
        margin-top: 30px;

        .puf-verify-content {
            padding: 56px;
            border-radius: 8px;
            border: 1px solid var(--Color-gray-200, #E3E8EF);
            max-width: 960px;
            margin-left: auto;
            margin-right: auto;
        }

        h1 {
            color: #000;
            font-family: var(--font-family-Inter, Inter);
            font-size: var(--font-size-hxl, 36px);
            font-style: normal;
            font-weight: 400;
            line-height: 122%; /* 43.92px */
            letter-spacing: -0.72px;
            margin-bottom: 15px;
        }

        p.puf-copy {
            color: #000;
            font-family: var(--font-family-Inter, Inter);
            font-size: var(--font-size-tl, 18px);
            font-style: normal;
            font-weight: 400;
            line-height: 156%; /* 28.08px */
            letter-spacing: -0.18px;
            margin-bottom: 20px;
        }
    }

    .view-report-verify-info-form {
        @include desktop-form-container()
    }

    ::v-deep .myriad-validation-label {
        min-width: 149px;
    }

    ::v-deep .myriad-container-warning-notice .demographic-dob-warning {
        margin-left: 162px;
    }

    .input-row.is-vertical {
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        > .input-label {
            margin-bottom: 8px;
            text-align: left;
        }

        .text-input {
            width: 100%;
            max-width: 160px;
        }
    }

    .puf-button {
        background: #0092E1;
        width: 200px;
    }

    .puf-button .submit-btn-text {
        left: 0;
    }

    @include small-desktop() {
        .puf-verify-page {
            @include site-section-sm-desktop();
        }
    }
    
    @include tablet() {
        .puf-verify-page {
            @include view-report-tablet-padding();
            min-height: calc(100vh - 381px);
        }

        ::v-deep .myriad-container-warning-notice .demographic-dob-warning {
            margin-left: 0;
        }

        .puf-button {
            margin-left: 0;
        }
    }

    @include sm() {
        .puf-verify-page {
            min-height: none;
        }

        ::v-deep .dob-component {
            margin-left: 0;
        }

        .puf-button {
            margin-left: 0;
        }
    }
</style>
